/**
 * @fileoverview Hospital Medical Specialties Constants
 * @description Predefined list of valid medical specialties for MCHD Room Scheduler
 * @author Hospital Room Scheduler Team
 * @created 08-10-2025 01:56:24
 *
 * This file defines all valid medical specialties that can be:
 * - Assigned to doctors (user.specialty)
 * - Tagged on rooms (room.specialtyTags)
 * - Used for access control (room.allowedSpecialties)
 *
 * This ensures consistent specialty validation across backend and frontend
 */
/**
 * Valid medical specialties recognized by the hospital system
 * Based on Saudi Ministry of Health medical specialty classifications
 */
export const VALID_SPECIALTIES = [
    'CARDIOLOGY',
    'NEUROLOGY',
    'ORTHOPEDICS',
    'PEDIATRICS',
    'GENERAL_SURGERY',
    'INTERNAL_MEDICINE',
    'EMERGENCY_MEDICINE',
    'ANESTHESIOLOGY',
    'RADIOLOGY',
    'OBSTETRICS_GYNECOLOGY',
    'PSYCHIATRY',
    'DERMATOLOGY',
    'OPHTHALMOLOGY',
    'ENT',
    'UROLOGY',
    'NEPHROLOGY',
    'GASTROENTEROLOGY',
    'ENDOCRINOLOGY',
    'RHEUMATOLOGY',
    'PULMONOLOGY',
    'HEMATOLOGY',
    'ONCOLOGY',
    'INFECTIOUS_DISEASE',
    'FAMILY_MEDICINE',
    'INTENSIVE_CARE',
    'PLASTIC_SURGERY',
    'VASCULAR_SURGERY',
    'CARDIOTHORACIC_SURGERY',
    'NEUROSURGERY',
    'PEDIATRIC_SURGERY',
    'PATHOLOGY',
    'NUCLEAR_MEDICINE',
    'REHABILITATION',
    'PAIN_MANAGEMENT',
    'GERIATRICS',
    'SPORTS_MEDICINE',
];
/**
 * Human-readable specialty display names
 * Maps specialty codes to user-friendly names for UI display
 */
export const SPECIALTY_DISPLAY_NAMES = {
    CARDIOLOGY: 'Cardiology',
    NEUROLOGY: 'Neurology',
    ORTHOPEDICS: 'Orthopedics',
    PEDIATRICS: 'Pediatrics',
    GENERAL_SURGERY: 'General Surgery',
    INTERNAL_MEDICINE: 'Internal Medicine',
    EMERGENCY_MEDICINE: 'Emergency Medicine',
    ANESTHESIOLOGY: 'Anesthesiology',
    RADIOLOGY: 'Radiology',
    OBSTETRICS_GYNECOLOGY: 'Obstetrics & Gynecology',
    PSYCHIATRY: 'Psychiatry',
    DERMATOLOGY: 'Dermatology',
    OPHTHALMOLOGY: 'Ophthalmology',
    ENT: 'Ear, Nose & Throat (ENT)',
    UROLOGY: 'Urology',
    NEPHROLOGY: 'Nephrology',
    GASTROENTEROLOGY: 'Gastroenterology',
    ENDOCRINOLOGY: 'Endocrinology',
    RHEUMATOLOGY: 'Rheumatology',
    PULMONOLOGY: 'Pulmonology',
    HEMATOLOGY: 'Hematology',
    ONCOLOGY: 'Oncology',
    INFECTIOUS_DISEASE: 'Infectious Disease',
    FAMILY_MEDICINE: 'Family Medicine',
    INTENSIVE_CARE: 'Intensive Care',
    PLASTIC_SURGERY: 'Plastic Surgery',
    VASCULAR_SURGERY: 'Vascular Surgery',
    CARDIOTHORACIC_SURGERY: 'Cardiothoracic Surgery',
    NEUROSURGERY: 'Neurosurgery',
    PEDIATRIC_SURGERY: 'Pediatric Surgery',
    PATHOLOGY: 'Pathology',
    NUCLEAR_MEDICINE: 'Nuclear Medicine',
    REHABILITATION: 'Rehabilitation',
    PAIN_MANAGEMENT: 'Pain Management',
    GERIATRICS: 'Geriatrics',
    SPORTS_MEDICINE: 'Sports Medicine',
};
/**
 * Specialty categories for grouping in UI
 * Helps organize specialties into logical groups for better UX
 */
export const SPECIALTY_CATEGORIES = {
    SURGICAL: [
        'GENERAL_SURGERY',
        'CARDIOTHORACIC_SURGERY',
        'NEUROSURGERY',
        'ORTHOPEDICS',
        'PLASTIC_SURGERY',
        'VASCULAR_SURGERY',
        'UROLOGY',
        'PEDIATRIC_SURGERY',
        'ENT',
        'OPHTHALMOLOGY',
    ],
    MEDICAL: [
        'CARDIOLOGY',
        'NEUROLOGY',
        'INTERNAL_MEDICINE',
        'GASTROENTEROLOGY',
        'ENDOCRINOLOGY',
        'NEPHROLOGY',
        'PULMONOLOGY',
        'RHEUMATOLOGY',
        'HEMATOLOGY',
        'ONCOLOGY',
        'INFECTIOUS_DISEASE',
        'GERIATRICS',
    ],
    ACUTE_CARE: [
        'EMERGENCY_MEDICINE',
        'INTENSIVE_CARE',
        'ANESTHESIOLOGY',
    ],
    PRIMARY_CARE: [
        'FAMILY_MEDICINE',
        'PEDIATRICS',
    ],
    DIAGNOSTIC: [
        'RADIOLOGY',
        'PATHOLOGY',
        'NUCLEAR_MEDICINE',
    ],
    OTHER: [
        'PSYCHIATRY',
        'DERMATOLOGY',
        'OBSTETRICS_GYNECOLOGY',
        'REHABILITATION',
        'PAIN_MANAGEMENT',
        'SPORTS_MEDICINE',
    ],
};
/**
 * Validate if a string is a valid specialty
 * @param specialty - Specialty string to validate
 * @returns True if specialty is valid, false otherwise
 *
 * @example
 * isValidSpecialty('CARDIOLOGY') // true
 * isValidSpecialty('INVALID') // false
 */
export function isValidSpecialty(specialty) {
    return VALID_SPECIALTIES.includes(specialty);
}
/**
 * Validate an array of specialties
 * @param specialties - Array of specialty strings
 * @returns Object with validation result and invalid specialties
 *
 * @example
 * validateSpecialties(['CARDIOLOGY', 'INVALID'])
 * // Returns: { isValid: false, invalidSpecialties: ['INVALID'] }
 */
export function validateSpecialties(specialties) {
    const invalidSpecialties = [];
    const validSpecialties = [];
    for (const specialty of specialties) {
        if (isValidSpecialty(specialty)) {
            validSpecialties.push(specialty);
        }
        else {
            invalidSpecialties.push(specialty);
        }
    }
    return {
        isValid: invalidSpecialties.length === 0,
        invalidSpecialties,
        validSpecialties,
    };
}
/**
 * Get display name for a specialty
 * @param specialty - Specialty code
 * @returns Human-readable display name or original code if not found
 *
 * @example
 * getSpecialtyDisplayName('CARDIOLOGY') // 'Cardiology'
 */
export function getSpecialtyDisplayName(specialty) {
    if (isValidSpecialty(specialty)) {
        return SPECIALTY_DISPLAY_NAMES[specialty];
    }
    return specialty;
}
/**
 * Get all specialties formatted for dropdown/select components
 * @returns Array of {value, label} objects for UI components
 *
 * @example
 * getSpecialtiesForSelect()
 * // Returns: [{ value: 'CARDIOLOGY', label: 'Cardiology' }, ...]
 */
export function getSpecialtiesForSelect() {
    return VALID_SPECIALTIES.map(specialty => ({
        value: specialty,
        label: SPECIALTY_DISPLAY_NAMES[specialty],
    })).sort((a, b) => a.label.localeCompare(b.label)); // Alphabetical order
}
/**
 * Get specialties grouped by category for UI display
 * @returns Object with specialty categories and their specialties
 *
 * @example
 * getSpecialtiesByCategory()
 * // Returns: { SURGICAL: [...], MEDICAL: [...], ... }
 */
export function getSpecialtiesByCategory() {
    const result = {};
    for (const [category, specialties] of Object.entries(SPECIALTY_CATEGORIES)) {
        result[category] = specialties.map(specialty => ({
            value: specialty,
            label: SPECIALTY_DISPLAY_NAMES[specialty],
        }));
    }
    return result;
}
/**
 * Filter specialties by search query
 * @param query - Search query string
 * @returns Array of matching specialties with their labels
 *
 * @example
 * searchSpecialties('cardio')
 * // Returns: [{ value: 'CARDIOLOGY', label: 'Cardiology' }, { value: 'CARDIOTHORACIC_SURGERY', label: 'Cardiothoracic Surgery' }]
 */
export function searchSpecialties(query) {
    const lowerQuery = query.toLowerCase();
    return VALID_SPECIALTIES
        .filter(specialty => {
        const label = SPECIALTY_DISPLAY_NAMES[specialty].toLowerCase();
        const value = specialty.toLowerCase();
        return label.includes(lowerQuery) || value.includes(lowerQuery);
    })
        .map(specialty => ({
        value: specialty,
        label: SPECIALTY_DISPLAY_NAMES[specialty],
    }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
//# sourceMappingURL=specialties.js.map